import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import ErrorBoundary from '../../../molecules/ErrorBoundary'
import FadeIn from '../../../molecules/Transitions/FadeIn'
import { planning } from '../../../../api/integrations'
import SydLabel from '../../../commonDesign/SydLabel'
import PointRing from './PointRing'

const useStyles = makeStyles({
  riskProfile: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  overallScore: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    padding: '2rem 0'
  },
  scores: {
    display: 'flex',
    gap: '1rem'
  },
  score: {
    display: 'flex',
    flex: 1,
    gap: '1rem'
  }
})

function Throw ({ error }) {
  throw error
}

function RiskProfileView ({
  clientId,
  pageTitle,
  categories,
  overallScore,
  styles: propStyles
}) {
  const defaultStyles = useStyles()
  const styles = {
    ...defaultStyles,
    ...(propStyles ?? {})
  }

  const {
    data,
    isLoading,
    isError,
    error
  } = planning.usePlanningQuery({
    queryType: 'riskprofile3d',
    payload: { clientId }
  })

  const chartData = useMemo(() => {
    if (isLoading || isError) return {}

    return {
      color: overallScore?.color ?? '#3376cd',
      overallScore: data.score,
      categories: [
        {
          title: categories.capacity.title,
          description: data.capacity.description,
          score: data.capacity.score,
          color: categories.capacity.color
        },
        {
          title: categories.tolerance.title,
          description: data.tolerance.description,
          score: data.tolerance.score,
          color: categories.tolerance.color
        },
        {
          title: categories.composure.title,
          description: data.composure.description,
          score: data.composure.score,
          color: categories.composure.color
        }
      ]
    }
  }, [isLoading, isError, overallScore?.color, data, categories])

  if (isLoading) return null
  if (isError) {
    return (
      <ErrorBoundary name='3D Risk Profile'>
        <Throw error={error} />
      </ErrorBoundary>
    )
  }

  return (
    <ErrorBoundary name={pageTitle}>
      <FadeIn>
        <div className={styles.riskProfile}>
          <div className={styles.overallScore}>
            <PointRing
              color={chartData.color}
              value={data.score}
              ringSeriesId='overallScore'
              pointSeriesId='overallScorePoint'
              chartProviderProps={overallScore.chartOptions}
              startAngle={overallScore.startAngle}
              endAngle={overallScore.endAngle}
            />
            <SydLabel label='Your total risk score includes your capacity, composure, and tolerance with taking risk' />
          </div>
          <div className={styles.scores}>
            {chartData.categories.map(cat => (
              <div className={styles.score} key={cat.title}>
                <div className='__value'>
                  <PointRing
                    color={cat.color}
                    value={cat.score}
                    ringSeriesId={`${cat.title}Score`}
                    pointSeriesId={`${cat.title}ScorePoint`}
                    chartProviderProps={categories.chartOptions}
                    startAngle={categories.startAngle}
                    endAngle={categories.endAngle}
                  />
                </div>
                <div className='__description'>
                  <SydLabel label={cat.title} />
                  <p>{cat.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </FadeIn>
    </ErrorBoundary>
  )
}

RiskProfileView.propTypes = {
  clientId: PropTypes.number,
  categories: PropTypes.object,
  overallScore: PropTypes.object,
  pageTitle: PropTypes.string,
  styles: PropTypes.object
}

RiskProfileView.defaultProps = {
  pageTitle: '3D Risk Profile',
  categories: {
    capacity: { title: 'Capacity', color: '#8963f0' },
    tolerance: { title: 'Tolerance', color: '#db7245' },
    composure: { title: 'Composure', color: '#4fa7b4' },
    startAngle: 90,
    endAngle: -270,
    chartOptions: {
      height: 100,
      width: 100
    }
  },
  overallScore: {
    color: '#3376cd',
    startAngle: 90,
    endAngle: -270,
    chartOptions: {
      height: 150,
      width: 150
    }
  },
  styles: {}
}

export default RiskProfileView
